import { useState, useEffect } from 'react';
import kit from './kit.module.css';
import styles from './NoScreenOrientationDemo.module.css';

// ── Shared data ───────────────────────────────────────────────────────────────

type State = 'idle' | 'playing' | 'done';

interface Message {
  role: 'user' | 'agent';
  text: string;
  highlight?: string;
}

const MESSAGES: Message[] = [
  { role: 'user', text: 'Add milk to my shopping list' },
  { role: 'agent', text: 'Done - milk added. You now have 1 item on your list. Want to add more, hear the list, or save it?', highlight: 'Confirms action + offers next steps' },
  { role: 'user', text: 'Add eggs and bread' },
  { role: 'agent', text: 'Added eggs and bread. 3 items total now: milk, eggs, bread. Add more, hear the full list, or save?', highlight: 'Recaps current state' },
  { role: 'user', text: 'Where am I?' },
  { role: 'agent', text: "You're building a shopping list. 3 items so far. You can add more, hear them read back, or say done to save and finish.", highlight: 'Answers the orientation question' },
  { role: 'user', text: 'Done' },
  { role: 'agent', text: "List saved with 3 items. I'll remind you when you open the shopping app. Anything else?", highlight: 'Confirms completion + offers exit' },
];

const DELAY_MS = 1200;

const SUMMARY_POINTS = [
  'Confirmed each action immediately',
  'Offered next steps after every response',
  'Answered the "where am I?" orientation question',
  'Kept responses short and easy to follow by ear',
];

// Exchanges used in the screen-hints scenario
const SCREEN_EXCHANGES = [
  { user: 'Add milk to my shopping list', agent: 'Done - milk added. 1 item on your list. Add more, hear it, or save?' },
  { user: 'Add eggs and bread', agent: 'Added eggs and bread. 3 items total: milk, eggs, bread.' },
  { user: 'Where am I?', agent: "You're building a shopping list. 3 items so far. Say done to save." },
];

// ── Transcript scenario ───────────────────────────────────────────────────────

function TranscriptScenario() {
  const [state, setState] = useState<State>('idle');
  const [run, setRun] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (state !== 'playing') return;
    setVisibleCount(0);
    const timers = MESSAGES.map((_, i) =>
      setTimeout(() => {
        setVisibleCount(i + 1);
        if (i === MESSAGES.length - 1) timers.push(setTimeout(() => setState('done'), DELAY_MS));
      }, i * DELAY_MS)
    );
    return () => timers.forEach(clearTimeout);
  }, [state, run]);

  function play() {
    setVisibleCount(0);
    setState('playing');
    setRun((r) => r + 1);
  }

  const agentThinking =
    state === 'playing' && visibleCount < MESSAGES.length && MESSAGES[visibleCount]?.role === 'agent';

  if (state === 'idle') {
    return (
      <>
        <div className={kit.orb} aria-hidden="true" />
        <p className={`${kit.feedback} ${styles.center}`}>
          See how a well-designed voice agent keeps users oriented without a screen.
        </p>
        <div className={`${kit.row} ${styles.centerRow}`}>
          <button className={`${kit.btn} ${kit.btnPrimary}`} onClick={play}>
            ▶ Play conversation
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={`${kit.orb} ${state === 'playing' ? kit.orbActive : ''}`} aria-hidden="true" />
      <div className={`${kit.row} ${styles.centerRow}`}>
        <span className={kit.chip}>Shopping list conversation</span>
      </div>

      <div className={kit.transcript} aria-live="polite">
        {MESSAGES.slice(0, visibleCount).map((msg, i) => (
          <div key={`${run}-${i}`} className={`${kit.voiceLine} ${styles.line} ${msg.role === 'agent' ? kit.voiceAgent : ''}`}>
            <span className={kit.voiceWho}>{msg.role === 'agent' ? 'Agent' : 'You'}</span>
            <div>
              <p>{msg.text}</p>
              {msg.highlight && <span className={styles.technique}>{msg.highlight}</span>}
            </div>
          </div>
        ))}
        {agentThinking && <div className={kit.cue}>(thinking...)</div>}
      </div>

      {state === 'done' && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>What worked</span>
            <span>4 techniques</span>
          </div>
          <ul className={styles.summary}>
            {SUMMARY_POINTS.map((point) => (
              <li key={point}>
                <span className={styles.check} aria-hidden="true">✓</span>
                {point}
              </li>
            ))}
          </ul>
          <div className={kit.row}>
            <button className={`${kit.btn} ${kit.btnEdit}`} onClick={play}>
              ↻ Replay
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Screen hints scenario ─────────────────────────────────────────────────────

type ScreenPhase = 'idle' | 'user' | 'agent' | 'done';

function ScreenHintsScenario() {
  const [exchangeIndex, setExchangeIndex] = useState(-1);
  const [phase, setPhase] = useState<ScreenPhase>('idle');

  useEffect(() => {
    if (phase === 'user') {
      const t = setTimeout(() => setPhase('agent'), 2000);
      return () => clearTimeout(t);
    }
    if (phase === 'agent') {
      const next = exchangeIndex + 1;
      const t = setTimeout(() => {
        if (next < SCREEN_EXCHANGES.length) {
          setExchangeIndex(next);
          setPhase('user');
        } else {
          setPhase('done');
        }
      }, 2800);
      return () => clearTimeout(t);
    }
  }, [phase, exchangeIndex]);

  function handlePlay() {
    setExchangeIndex(0);
    setPhase('user');
  }

  const exchange = SCREEN_EXCHANGES[exchangeIndex];
  const speaking = phase === 'user' || phase === 'agent';
  const isAgent = phase === 'agent';

  return (
    <>
      <div className={styles.screen}>
        <div className={styles.screenTop}>
          <span className={kit.label}>Smart display</span>
          <span className={kit.label}>12:00</span>
        </div>

        <div className={styles.screenBody}>
          <div className={`${kit.orb} ${speaking ? kit.orbActive : ''} ${phase === 'user' ? styles.orbListening : ''}`} aria-hidden="true" />

          {phase === 'idle' && <p className={`${kit.feedback} ${styles.center}`}>Captions appear here while you talk.</p>}

          {speaking && (
            <span className={`${kit.label} ${isAgent ? styles.agentLabel : ''}`}>
              {isAgent ? 'Assistant is speaking' : 'Listening to you...'}
            </span>
          )}

          {phase === 'done' && <span className={`${kit.chip} ${kit.chipGood}`}>✓ List saved</span>}
        </div>

        {speaking && exchange && (
          <div className={`${styles.caption} ${isAgent ? styles.captionAgent : ''}`} aria-live="polite">
            <span className={kit.voiceWho}>{isAgent ? 'Assistant' : 'You'}</span>
            <p>{isAgent ? exchange.agent : exchange.user}</p>
          </div>
        )}
      </div>

      <div className={`${kit.row} ${styles.centerRow}`}>
        {phase === 'idle' && (
          <button className={`${kit.btn} ${kit.btnPrimary}`} onClick={handlePlay}>
            ▶ Play with captions
          </button>
        )}
        {phase === 'done' && (
          <button className={`${kit.btn} ${kit.btnEdit}`} onClick={handlePlay}>
            ↻ Replay
          </button>
        )}
        {speaking && (
          <p className={kit.feedback}>
            Exchange {exchangeIndex + 1} of {SCREEN_EXCHANGES.length} - {isAgent ? 'agent responding with a caption' : 'user speaking'}
          </p>
        )}
      </div>
    </>
  );
}

// ── Top-level component with scenario tabs ────────────────────────────────────

type Tab = 'transcript' | 'screen';

const TABS: { id: Tab; label: string }[] = [
  { id: 'transcript', label: 'Conversation' },
  { id: 'screen', label: 'With screen hints' },
];

export function NoScreenOrientationDemo() {
  const [tab, setTab] = useState<Tab>('transcript');

  return (
    <div className={kit.root}>
      <div className={kit.row} role="tablist" aria-label="Scenario">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`${kit.chip} ${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div key={tab} className={kit.stack}>
        {tab === 'transcript' ? <TranscriptScenario /> : <ScreenHintsScenario />}
      </div>
    </div>
  );
}
